import { useState, useRef, useEffect } from "react";
import { IImpersonationBannerViewModel, ILinkModel } from 'Types/Litium';
import ArrowDown from '@Alpha/Images/arrow-down.svg?react';
import ProfileIcon from 'Icons/profile.svg?react';
import MagnicifactionGlassIcon from 'Icons/magnificationGlass.svg?react';
import useOutsideClick from 'Hooks/useOutsideClick';
import useDebounce from 'Hooks/useDebounce';
import {type User} from 'Services/impersonateApi';
import { usePersonSearchMutation } from 'Services/personSearchApi';
import { useImpersonateMutation, useDeimpersonateMutation } from 'Services/impersonateApi';
import ImpersonationBannerSearchResult from './ImpersonationBannerSearchResult';
import Loader from 'Components/Loader';

interface UserOption {
    label: string;
    value: string;
}

interface ImpersonationBanner extends IImpersonationBannerViewModel {
    loginPage: ILinkModel;
}

const ImpersonationBanner = ({
    translations,
    currentUser,
    isImpersonating,
    loginPage,
}: ImpersonationBanner) => {
    const [showDropdown, toggleDropdown] = useState(false);
    const impersonationSelectRef = useRef<HTMLDivElement>(null);
    const [personSearchQuery, setPersonSearchQuery] = useState('');
    const [impersonatedPerson, setImpersonatedPerson] = useState<UserOption>({label: '', value: ''});
    const [impersonating, setImpersonating] = useState(isImpersonating);
    const [showLoading, setShowLoading] = useState(false);
    const debouncedSearch = useDebounce(personSearchQuery, 1000);

    const impersonationDropdownRef = useOutsideClick(
        () => toggleDropdown(false),
        impersonationSelectRef
    );

    const impersonateSelectedUser = (systemId) => {
        impersonateUser(systemId);
    };

    const preparePersonSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPersonSearchQuery(e.currentTarget.value.trim());
    };

    const [
        impersonateUser,
        {
            data: impersonateResults,
            isSuccess: impersonateSuccess,
            isError: impersonateError },
    ] = useImpersonateMutation();

    const [
        deimpersonateUser,
        {
            data: deimpersonateResults,
            isSuccess: deimpersonateSuccess,
            isError: deimpersonateError
         },
    ] = useDeimpersonateMutation();

    const [
        getPersonSearchResult,
        { 
            data: personSearchResults,
            isSuccess: searchSuccess,
            isLoading: searchLoading,
            isError: searchError
        },
    ] = usePersonSearchMutation();

    useEffect(() => {
        setImpersonatedPerson(getUserOption(currentUser));
    }, [currentUser]);

    useEffect(() => {
        if (debouncedSearch) getPersonSearchResult(personSearchQuery);
    }, [debouncedSearch]);

    useEffect(() => {
        if (searchLoading) setShowLoading(true);
        if (searchSuccess || searchError) setShowLoading(false);
    }, [searchLoading, searchSuccess, searchError]);

    useEffect(() => {
        if (impersonateSuccess) {
            setImpersonatedPerson(getUserOption(impersonateResults))
            setImpersonating(true);
        };
        toggleDropdown(false);
    }, [impersonateSuccess]);

    useEffect(() => {
        if (deimpersonateSuccess) {
            setImpersonatedPerson(getUserOption(deimpersonateResults))
            setImpersonating(false);
        };
    }, [deimpersonateSuccess]);

    const getUserOption = (user: User) => {
        let textContent = '';

        if(user.firstName && user.lastName) {
            textContent = `${user.firstName} ${user.lastName}`;
            
        } else if((user.firstName || user.lastName) && user.email) {
            textContent = `${user.firstName ? user.firstName: user.lastName} ${user.email}`;

        } else if((user.firstName || user.lastName) && user.phone) {
            textContent = `${user.firstName ? user.firstName: user.lastName} ${user.phone}`;

        } else if(user.email || user.phone) {
            textContent = user.email ? user.email : user.phone;
        }

        return {label: textContent, value: user.systemId};
    };

    const toggleSearch = (event: React.KeyboardEvent) => {
        if (event.code === 'Enter' || event.code === 'Space') {
            event.preventDefault();
            toggleDropdown(!showDropdown);
        }
        if (event.code === 'Tab') {
            toggleDropdown(false);
        }
    };

    const closeSearch = (event: React.KeyboardEvent) => {
        if (event.code === 'Tab' && !searchSuccess) {
            toggleDropdown(false);
        }
    };

return (
    <div className="grid grid-cols-4 h-12 p-1.5">
        <div className="flex justify-center col-span-2 items-center">
            {loginPage?.href && (
                <a
                    className="mr-2"
                    aria-label={loginPage?.text}
                    href={loginPage.href}
                >
                    <ProfileIcon className="h-6 w-6" />
                </a>
            )}
            <div className="relative text-sm font-montserrat">
                <div
                    className="rounded-sm cursor-pointer border border-[#D5D3DE] w-80 h-full py-2 pl-3 pr-9 relative select-none"
                    onClick={() => toggleDropdown(!showDropdown)}
                    onKeyDown={toggleSearch}
                    ref={impersonationSelectRef}
                    tabIndex={0}
                >
                    {impersonating ? impersonatedPerson.label : translations.default}
                    <ArrowDown className={
                        `absolute right-3 top-1.5 pointer-events-none ${
                        showDropdown &&
                        'rotate-[180deg]'
                    }`}/>
                </div>
                {showDropdown && 
                    <div
                        className="bg-white border-x border-[#D5D3DE] z-50 absolute w-full"
                        ref={impersonationDropdownRef as React.MutableRefObject<HTMLDivElement>}
                    >
                        <div className="h-full w-full text-black py-2.5 px-2.5 border-b border-[#D5D3DE] relative">
                            <input
                                autoFocus
                                type="search"
                                className="h-full w-full placeholder:text-[#6D6D8E] text-black py-2.5 pl-3.5 pr-11 border rounded-sm focus-within:border-[#015ECC] focus-within:outline-0"
                                placeholder={translations.search}
                                onKeyDown={closeSearch}
                                onChange={preparePersonSearch}
                            />
                            <MagnicifactionGlassIcon className="absolute right-5 top-4.5 pointer-events-none h-6 text-current"/>
                            {impersonateError &&
                                <div className="mt-1.5 text-red-600 text-xs">
                                    {translations.impersonateError}
                                </div>
                            }
                        </div>
                        <div>
                            {searchSuccess && personSearchResults?.length > 0 && personSearchResults.map((item) => (
                                <ImpersonationBannerSearchResult
                                    key={item.systemId}
                                    id={item.id}
                                    systemId={item.systemId}
                                    textContent={getUserOption(item).label}
                                    onClickHandler={impersonateSelectedUser}
                                    selected={item.systemId === impersonatedPerson.value}
                                />
                            ))}
                            {searchSuccess && personSearchResults?.length === 0 && (
                                <div className="h-full w-full text-black py-2.5 px-2.5 border-b border-[#D5D3DE]" data-result="empty">
                                    {translations.searchEmpty}
                                </div>
                            )}
                            {searchError && (
                                <div className="h-full w-full text-black py-2.5 px-2.5 border-b border-[#D5D3DE]" data-result="error">
                                    {translations.searchError}
                                </div>
                            )}
                            {showLoading && (
                                <div className="h-full w-full text-black py-2.5 px-2.5 border-b border-[#D5D3DE]" data-loading="true">
                                    <Loader size="10px" marginY="my-2" />
                                </div>
                            )}
                        </div>
                    </div>
                }
            </div>
            {impersonating &&
                <div className="flex items-center gap-2 px-2 relative">
                    <button
                        className="cursor-pointer underline"
                        onClick={() => deimpersonateUser()}
                    >
                        {translations.clear}
                    </button>
                    {deimpersonateError &&
                        <div className="absolute left-[100%] top-3 text-red-600 text-xs whitespace-nowrap">
                            {translations.deimpersonateError}
                        </div>
                    }
                </div>
            }
        </div>
    </div>
)};

export default ImpersonationBanner;
